import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MarketScan, Category } from '../../classes/market-scan/market-scan.class';
import { Endpoints } from '../../../config/endpoints.config';
import { AuthenticationService } from '../../services/authentication.service';
import { DynamicsClientService } from '../dynamics-client/dynamics-client.service';
import { fetchXmlEntity, fetchQueries } from '@omni/config/dynamics-fetchQueries';
import { BrandOfflineService } from '../../services/brand/brand.service';

@Injectable({
  providedIn: 'root'
})
export class MarketScanDataService {
  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private dynamics: DynamicsClientService,
    private brandOfflineService: BrandOfflineService) {
  }

  public async getMarketScans(url: string): Promise<MarketScan[]> {
    return await this.http.get<MarketScan[]>(url, Endpoints.GLOBAL_SYNC_HEADER).toPromise();
  }

  public async getMarketScanCategories(url: string): Promise<Category[]> {
    return await this.http.get<Category[]>(url, Endpoints.GLOBAL_SYNC_HEADER).toPromise();
  }

  // Small, separately-cacheable master list of active indskr_customersegmentation records
  // (id -> name). Mirrors AdoptionLadderJourneyService.getAdoptionLadderData()'s pattern —
  // fetched/cached independently of the high-volume productrating query below.
  public async getCustomerSegmentationMasterData(): Promise<{ id: string; name: string }[]> {
    const fetchXml = fetchQueries.fetchCustomerSegmentationMasterData;
    const response = await this.dynamics.executeFetchQuery('indskr_customersegmentations', fetchXml);
    return (response ?? []).map((item: any) => ({
      id: item.indskr_customersegmentationid,
      name: item.indskr_name,
    }));
  }

  /** FetchXML <value> list built from `user.ladderTypeIds` — the list of ladder-type
   * (indskr_masterdata) IDs the current user's security role(s) are mapped to, via
   * indskr_laddersecurityrolesmapping. Resolved once by AuthenticationDataService.getUser()
   * (on login and on every sync) and stored on User, the same as ioConfigurations/featureActions/
   * orgsettings — not fetched or cached here, so it can never go stale across a user switch or
   * outlive a sync the way a standalone service-level cache would. Returns a non-matching dummy
   * GUID when the user has no mapped ladder types, so an "in" filter stays valid and correctly
   * excludes every row rather than becoming an empty/invalid list.
   *
   * Resolved separately from the big indskr_productrating queries below rather than joined inline
   * against them — that inline join was confirmed (via a live SQL timeout on this org) to push an
   * already-expensive query over Dataverse's execution limit, since indskr_productrating is very
   * large here. Callers filter the big table with a plain indskr_ladder IN (...) condition built
   * from this list instead. */
  public buildLadderTypeIdValues(): string {
    const ladderTypeIds = this.authService.user.ladderTypeIds ?? [];
    return ladderTypeIds.length > 0
      ? ladderTypeIds.map(id => `<value>${id}</value>`).join('')
      : '<value>00000000-0000-0000-0000-000000000000</value>';
  }

  /**
   * Shared indskr_productrating FetchXML skeleton for the segmentation-only and ladder-only
   * fetches below. indskr_segmentation and indskr_adoptionladder always live on mutually
   * exclusive rows (confirmed with product team — this entity is split by row, not by column),
   * so each caller adds exactly one not-null condition to scope itself to its own row type; no
   * symmetric opposite-field exclusion is needed. Neither variant takes a date-range/offline-
   * duration bound — indskr_productrating is a current-state classification, not a transactional
   * record, so there is no "how far back" concept to apply here.
   *
   * @param extraRootCondition FetchXML <condition> narrowing rows to one type (segmentation vs
   * adoption ladder), spliced into the root <filter type="and">.
   * @param lastSyncedTimestamp when provided, fetches only rows modified on/after this time
   * (epoch ms, matching the pattern used by syncMarketScans' lastUpdatedTime) instead of the
   * full dataset. When provided, the statecode='0' filter is also dropped so that rows which
   * became inactive since last sync are still returned (with their real statecode) — callers
   * need those to know to remove them, not just rows that are newly active.
   */
  private buildProductRatingFetchXml(extraRootCondition: string, lastSyncedTimestamp?: string): string {
    // Get user position IDs for filtering data based on user's access
    const positionIDs = this.authService.user.positions.map((o) => o.ID);

    // Build position condition for FetchXML query
    const positionCondition = positionIDs.length > 1 ? positionIDs.map(id => `<value>${id}</value>`).join('') : (positionIDs[0] ?? null);

    // Helper function to create position filter conditions for FetchXML
    const positionFilter = (attribute: string, entity?: string) =>
      positionIDs.length > 1
        ? `<condition ${entity ? `entityname="${entity}"` : ''} attribute="${attribute}" operator="in">${positionCondition}</condition>`
        : `<condition ${entity ? `entityname="${entity}"` : ''} attribute="${attribute}" operator="eq" value="${positionCondition}" />`;

    const isDelta = !!lastSyncedTimestamp;

    return [
      // count=5000: no explicit page size here meant DynamicsWebApi.fetchAll (which
      // executeFetchQuery wraps) fell back to Dataverse's own default page size,
      // producing far more sequential paging round-trips than necessary for a
      // 19k+-contact territory's full/initial adoption-ladder sync (each row is one
      // contact-per-product, so this can legitimately be tens of thousands of rows).
      // Matches the same fix already applied to fetchConfiguredFormEntity.
      '<fetch version="1.0" output-format="xml-platform" mapping="logical" distinct="true" no-lock="true" count="5000">',
      '<entity name="indskr_productrating">',

      '<attribute name="indskr_productratingid" />',
      '<attribute name="statecode" alias="rowStatecode" />',
      '<attribute name="indskr_adoptionladder" alias="adoptionLadderId" />',
      '<attribute name="indskr_segmentation" alias="segmentationId" />',
      '<attribute name="indskr_product" alias="productId" />',
      '<attribute name="indskr_customerid" alias="contactId" />',

      // Global merged filter
      '<filter type="and">',

      // Root entity filter: on a full fetch, only active rows. On a delta fetch, we need
      // recently-modified rows regardless of state (rowStatecode above tells the caller which
      // ones to remove) — a hard statecode filter here would hide exactly the deactivations
      // delta sync needs to detect.
      // lastSyncedTimestamp arrives as an epoch-ms string (new Date().getTime().toString(), the
      // same format the disk-persisted sync state stores) — Dataverse's modifiedon condition
      // needs ISO 8601, not a raw epoch number, or it 400s with "date-time format ... invalid".
      isDelta
        ? `<condition attribute="modifiedon" operator="on-or-after" value="${new Date(Number(lastSyncedTimestamp)).toISOString()}" />`
        : '<condition attribute="statecode" operator="eq" value="0" />',

      // Scopes this fetch to one row type (segmentation vs adoption ladder) — see method doc.
      extraRootCondition,

      // NOTE: the indskr_customersegmentation ("cs") join was removed here. It selected no
      // columns — segmentationId's display name already comes for free via the lookup
      // attribute's _Formatted annotation (see segmentationId_Formatted below) — it only
      // gated "linked segmentation record must be active". Dropping it means a productrating
      // whose linked segmentation has since gone inactive now stays visible instead of being
      // excluded row-wide (ladder data included). See Market Scan validation checklist before
      // shipping this specific behavior change.

      // Product eligibility (producttype 100000002, active, assigned to this position) is now
      // filtered client-side against BrandOfflineService.brands — see below — instead of joining
      // product/indskr_productassignment/position here.

      // Contact filters
      '<filter>',
      '<condition entityname="ag" attribute="statecode" operator="eq" value="0" />',
      '<condition entityname="ag" attribute="indskr_isguest" operator="eq" value="0" />',
      '</filter>',

      // Customer Position (alias ai)
      '<filter type="or">',
      positionFilter('positionid', 'ai'),
      positionFilter('parentpositionid', 'ai'),
      '</filter>',

      '</filter>',

      // Contact (statecode/isguest filter only — sibling of customerposition, not its parent)
      '<link-entity name="contact"',
      ' from="contactid"',
      ' to="indskr_customerid"',
      ' link-type="inner"',
      ' alias="ag">',
      '</link-entity>',

      // Customer Position — joins directly off indskr_customerid, no longer nested under contact.
      // indskr_customerposition's own lookup field to the contact is indskr_customerid (not
      // contactid) — confirmed by the Dataverse error when this was first written as "contactid".
      '<link-entity name="indskr_customerposition"',
      ' from="indskr_customerid"',
      ' to="indskr_customerid"',
      ' link-type="inner"',
      ' alias="ah">',

      '<link-entity name="position"',
      ' from="positionid"',
      ' to="indskr_positionid"',
      ' link-type="inner"',
      ' alias="ai">',
      '</link-entity>',

      '</link-entity>',

      '</entity>',
      '</fetch>',
    ].join('');
  }

  /**
   * Filters fetched indskr_productrating rows against product eligibility (common to both
   * split fetches), and — only when includeSegmentationFields is true — nulls out segmentation
   * id/name for any segmentationId not found in activeSegmentMap. On the ladder-only fetch,
   * segmentationId is always empty by construction (see buildProductRatingFetchXml's not-null
   * condition), so that branch is skipped entirely rather than running as dead code.
   */
  private filterAndMapRows(response: any[], activeSegmentMap?: Map<string, string>, includeSegmentationFields = true): any[] {
    // Product eligibility (producttype 100000002, active, assigned to this position) is already
    // guaranteed by BrandOfflineService.brands, which is fetched server-side scoped to the same
    // positions — filter here instead of joining product/indskr_productassignment/position above.
    // Rows being reported as removed (rowStatecode != 0) are exempt — they still need to reach
    // the merge step so a since-deactivated row gets removed regardless of product eligibility.
    const eligibleProductIds = new Set(
      (this.brandOfflineService.brands ?? [])
        .filter(brand => brand.producttype === 100000002)
        .map(brand => brand.ID)
    );
    response = response.filter((r: any) => r.rowStatecode !== 0 || eligibleProductIds.has(r.productId));

    return response.map((item: any) => {
      // No map passed in -> behave as a plain pass-through (defensive default).
      // Map passed in -> only keep segmentationId/name if it's a known active segment.
      const isKnownActiveSegment = !includeSegmentationFields
        ? false
        : !activeSegmentMap || (item.segmentationId && activeSegmentMap.has(item.segmentationId));
      return {
        indskr_productratingid: item.indskr_productratingid,
        rowStatecode: item.rowStatecode,
        indskr_customersegmentationid: isKnownActiveSegment ? item.segmentationId : null,
        indskr_name: isKnownActiveSegment ? (activeSegmentMap?.get(item.segmentationId) ?? item.segmentationId_Formatted) : null,
        productId: item.productId,
        contactId: item.contactId,
        adoptionLadderId: item.adoptionLadderId,
      };
    });
  }

  /**
   * Raw (ungrouped) indskr_productrating rows carrying segmentation only — the fast, awaited
   * sync path. See buildProductRatingFetchXml for the shared query shape and filterAndMapRows
   * for post-fetch eligibility/active-segment filtering.
   *
   * @param activeSegmentMap id -> name map of active indskr_customersegmentation records
   * (see getCustomerSegmentationMasterData). When provided, a segmentationId not found in the
   * map is treated as unset (name/id nulled) instead of excluding the whole productrating row —
   * equivalent in spirit to the old cs join's active-record check, computed client-side.
   */
  public async getSegmentationRatingsRaw(activeSegmentMap?: Map<string, string>, lastSyncedTimestamp?: string): Promise<any[]> {
    const fetchXML = this.buildProductRatingFetchXml(
      '<condition attribute="indskr_segmentation" operator="not-null" />',
      lastSyncedTimestamp);
    const response = await this.dynamics.executeFetchQuery('indskr_productratings', fetchXML);
    return this.filterAndMapRows(response, activeSegmentMap, true);
  }

  /**
   * Raw (ungrouped) indskr_productrating rows carrying adoption ladder only — the async,
   * fire-and-forget background path. See buildProductRatingFetchXml for the shared query shape
   * and filterAndMapRows for post-fetch eligibility filtering.
   *
   * Scoped by ladder-type/security-role: indskr_productrating's own indskr_ladder lookup points
   * to a ladder type (indskr_masterdata), and indskr_laddersecurityrolesmapping maps a ladder
   * type to the security role(s) that use it (e.g. "Affinity ladder"/"Sales Adoption ladder" ->
   * Sales User; "Medical Adoption ladder"/"Stakeholder Influence" -> Medical User). A row only
   * comes back if the CURRENT user holds a role mapped to its ladder type — cuts fetched volume
   * to only the ladder types this user actually needs. Scoped via a plain indskr_ladder IN (...)
   * condition (see buildLadderTypeIdValues, backed by User.ladderTypeIds) rather than joining
   * indskr_masterdata/indskr_laddersecurityrolesmapping inline here — that inline join was
   * confirmed via a live SQL timeout to push this already-expensive query over Dataverse's
   * execution limit. There is deliberately no "OR no mapping exists" fallback: a ladder type with
   * no role mapping configured at all is excluded rather than shown to everyone, since the whole
   * point is fetching only what's relevant to this user.
   */
  public async getAdoptionLadderRatingsRaw(lastSyncedTimestamp?: string): Promise<any[]> {
    const ladderTypeIdValues = this.buildLadderTypeIdValues();
    const fetchXML = this.buildProductRatingFetchXml(
      `<condition attribute="indskr_adoptionladder" operator="not-null" />`
      + `<condition attribute="indskr_ladder" operator="in">${ladderTypeIdValues}</condition>`,
      lastSyncedTimestamp);
    const response = await this.dynamics.executeFetchQuery('indskr_productratings', fetchXML);
    return this.filterAndMapRows(response, undefined, false);
  }

  public async getEvents(startDate: string, endDate: string): Promise<any> {
    let url = this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.marketScan.EVENTS;
    const positions = this.authService.user.positions.map((o) => { return o.ID });
    url = url.replace('{PositionIds}', positions.toString()).replace('{startDate}', startDate).replace('{endDate}', endDate);
    return this.http.get<any>(url, Endpoints.GLOBAL_SYNC_HEADER).toPromise();
  }

  public async getAffiliatedAccounts(): Promise<any> {
    let url = this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.marketScan.AFFILIATED_ACCOUNTS;
    const positions = this.authService.user.positions.map((o) => { return o.ID });
    url = url.replace('{PositionIds}', positions.toString());
    return this.http.get<any>(url, Endpoints.GLOBAL_SYNC_HEADER).toPromise();
  }

  public async getCampaigns(startDate: string, endDate: string): Promise<any> {
    let url = this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.marketScan.CUSTOMER_JOURNEYS;
    const positions = this.authService.user.positions.map((o) => { return o.ID });
    url = url.replace('{PositionIds}', positions.toString()).replace('{startDate}', startDate).replace('{endDate}', endDate);
    return this.http.get<any>(url, Endpoints.GLOBAL_SYNC_HEADER).toPromise();
  }

  public async createMarketScan(marketScan) {
    const url = this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.marketScan.CREATE_MARKET_SCAN;
    return this.http.patch<any>(url, marketScan, Endpoints.GLOBAL_SYNC_HEADER).toPromise();
  }

  public async updateMarketScan(marketScan) {
    let url = this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.marketScan.UPDATE_MARKET_SCAN;
    //url = url.replace('{marketScanId}', marketScan.indskr_marketscanid);
    return this.http.patch<any>(url, marketScan, Endpoints.GLOBAL_SYNC_HEADER).toPromise();
  }

  public async uploadOfflineMarketScan(marketScans) {
    const url = this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.marketScan.OFFLINE_UPLOAD_MARKET_SCAN;
    return this.http.post<any>(url, marketScans, Endpoints.GLOBAL_SYNC_HEADER).toPromise();
  }

  public async scrapMarketScan(id: string) {
    let url = this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.marketScan.DELETE_MARKET_SCAN;
    url = url.replace('{marketScanId}', id);
    return this.http.delete<any>(url, Endpoints.GLOBAL_SYNC_HEADER).toPromise();
  }


  public async getMarketScanDeltaById(url: string): Promise<MarketScan> {
    return this.http.get<MarketScan>(url, Endpoints.GLOBAL_SYNC_HEADER).toPromise();
  }

  public async getCustomerDataAttributes() {
    let hasEvents: boolean = false;
    let hasCampaigns: boolean = false;
    let url: string = this.authService.userConfig.activeInstance.url + "/api/data/v9.0/EntityDefinitions(LogicalName='indskr_marketscancustomerdata')/Attributes?$select=LogicalName&$filter=LogicalName eq 'indskr_eventsid' or LogicalName eq 'indskr_campaignsid'";
    const response = await this.http.get<any[]>(url).toPromise();
    // const response = await this.dynamics.retrieveAttributes('indskr_marketscancustomerdata', "", ['LogicalName'], "LogicalName eq 'indskr_eventsid' or LogicalName eq 'indskr_campaignsid'");
    if (response && response['value'] && response['value'].length > 0) {
      response['value'].forEach(v => {
        if (v['LogicalName'] === 'indskr_campaignsid') {
          hasCampaigns = true;
        } else {
          hasEvents = true;
        }
      })
    }
    return { hasEvents, hasCampaigns }

  }

  public async getCreateFrequency(): Promise<number> {
    const response = await this.dynamics.executeFetchQuery('businessunits', fetchXmlEntity({
      "@": { "name": "businessunit" },
      "attribute": [
        { "@": { "name": "indskr_customerscancreatefrequency", "alias": "createFrequency" } }
      ],
      "filter": [
        {
          "@": { "type": "and" },
          "condition": [
            { "@": { "attribute": "businessunitid", "operator": "eq", "value": this.authService.user.xBusinessUnitId } },
          ]
        }
      ],
      "link-entity": []
    }), null);
    if (response && response.length == 1) {
      return response[0]['createFrequency'];
    }
    return null;
  }
}
